import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";
import { MindARDynamicViewer } from "./RenderingComponents/MindARDynamicViewer";
import { Box, Button } from "@mui/material";
import { ArViewUI } from "./UI/ArViewUI";
import RefreshIcon from '@mui/icons-material/Refresh';
import MindARMarkerDetector from "./RenderingComponents/MindARMarkerDetector";
import Static3DAssetViewer from "./RenderingComponents/Static3DAssetViewer";
import Static2DAssetViewer from "./RenderingComponents/Static2DAssetViewer";
import ErrorBoundary from "./ErrorBoundary";
import { AppConfig } from "./config/AppConfig";
import { RenderStrategy, RenderMode } from "./types/enums";
import { PlaceholderOverlay } from "./UI/PlaceholderOverlay";
import MainMenu from "./UI/MainMenu";

const App: React.FC = () => {
    const [isARReady, setIsARReady] = useState(false);
    const [isAssetBeingRendered, setIsAssetBeingRendered] = useState(false);
    const [hideAssetTimer, setHideAssetTimer] = useState<number | null>(null);
    const [mindARViewerKey, setMindARViewerKey] = useState(0);
    const [currentTargetIndex, setCurrentTargetIndex] = useState<number | null>(null);
    const [markerFound, setMarkerFound] = useState(false);
    const [showMainMenu, setShowMainMenu] = useState(true);
    const [isAssetExiting, setIsAssetExiting] = useState(false);
    const assetViewerRef = useRef<{ handleClose: () => void } | null>(null);

    const assets = AppConfig.assets[AppConfig.version];
    const mindFileSrc = AppConfig.mindFiles[AppConfig.version];

    const handleAssetClose = useCallback(() => {
        setIsAssetBeingRendered(false);
        setCurrentTargetIndex(null);
        setMarkerFound(false);
    }, []);

    const handleARReady = () => {
        setIsARReady(true);
    };

    const handleMarkerFound = (targetIndex?: number) => {
        console.log(`Marker ${targetIndex} found`);
        if (hideAssetTimer !== null) {
            clearTimeout(hideAssetTimer);
            setHideAssetTimer(null);
        }

        if (!isAssetExiting) {
            setCurrentTargetIndex(targetIndex ?? null);
            setIsAssetBeingRendered(true);
            setMarkerFound(true);
        }
    };

    const handleMarkerLost = () => {
        if (!isAssetExiting) {
            const timer = window.setTimeout(() => handleAssetClose(), 200);
            setHideAssetTimer(timer);
        }
    };

    useEffect(() => {
        return () => {
            if (hideAssetTimer !== null) {
                clearTimeout(hideAssetTimer);
            }
        };
    }, [hideAssetTimer]);

    useEffect(() => {
        // Apply version-aware background color with overlay
        document.body.style.backgroundColor = `${AppConfig.theme[AppConfig.version].backgroundColor}57`; // 57 is hex for 0.34 opacity
        
        return () => {
            document.body.style.backgroundColor = '';
        };
    }, []);

    const handleResetTargetSearch = async () => {
        console.log("Resetting AR system and starting new target search...");
        if (assetViewerRef.current) {
            assetViewerRef.current.handleClose();
            setTimeout(() => {
                setMindARViewerKey((prevKey) => prevKey + 1);
            }, 800); // Wait for animation to complete
        }
    };

    return (
        <ErrorBoundary>
            <Box
                sx={{
                    width: '100vw',
                    height: '100vh',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    overflow: 'hidden'
                }}
            >
                {AppConfig.devMode && markerFound && (
                    <div
                        style={{
                            position: 'fixed',
                            top: '20px',
                            left: '20px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '10px',
                            borderRadius: '5px',
                            zIndex: 1000,
                            fontSize: '16px',
                        }}
                    >
                        Marker Found
                    </div>
                )}

                {isAssetBeingRendered && (
                    <Box
                        style={{
                            position: "absolute",
                            top: "90svh",
                            right: "5vw",
                            zIndex: 11,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleResetTargetSearch}
                            size="large"
                            style={{
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "calc(8px + 1.7vmin)",
                                padding: "10px 20px",
                                borderRadius: "10px",
                                boxShadow: "0 8px 12px rgba(0, 0, 0, 0.1)",
                                transition: "background-color 0.3s ease",
                                border: `2px solid ${AppConfig.theme[AppConfig.version].backgroundColor}`,
                                zIndex: 1000,
                                textTransform: "none",
                            }}
                        >
                            <RefreshIcon
                                style={{
                                    marginRight: '8px',
                                    fontSize: "calc(18px + 1.7vmin)",
                                }}
                            />
                            Scan another one
                        </Button>
                    </Box>
                )}

                {!isAssetBeingRendered && <ArViewUI
                    isVisible={!isAssetBeingRendered && !showMainMenu}
                    onBackToMenu={() => setShowMainMenu(true)}
                />}

                {AppConfig.renderStrategy === RenderStrategy.DYNAMIC && (
                    <MindARDynamicViewer
                        key={mindARViewerKey}
                        onMarkerFound={handleMarkerFound}
                        onMarkerLost={handleMarkerLost}
                        mindFileSrc={mindFileSrc}
                        assets={assets}
                        onARReady={handleARReady}
                    />
                )}

                {
                    showMainMenu && (
                        <MainMenu
                            onStartAR={() => setShowMainMenu(false)}
                            // onHowToInteract={() => setShowMainMenu(false)}
                        />
                    )
                }

                {AppConfig.renderStrategy === RenderStrategy.STATIC && (
                    <>
                        <MindARMarkerDetector
                            onMarkerFound={handleMarkerFound}
                            mindFileSrc={mindFileSrc}
                            onARReady={handleARReady}
                            assets={assets}
                            showMainMenu={showMainMenu}
                        />

                        {isAssetBeingRendered && currentTargetIndex !== null && (
                            AppConfig.renderMode === RenderMode.MODE_3D ? (
                                <Static3DAssetViewer
                                    glbModelSrc={
                                        assets.find((asset) => asset.targetIndex === currentTargetIndex)?.glbModelSrc || ""
                                    }
                                />
                            ) : (
                                <Static2DAssetViewer
                                    ref={assetViewerRef}
                                    glbModelSrc={
                                        assets.find((asset) => asset.targetIndex === currentTargetIndex)?.glbModelSrc || ""
                                    }
                                    onClose={handleAssetClose}
                                />
                            )
                        )}
                    </>
                )}

                {(!isARReady) && (
                    <PlaceholderOverlay version={AppConfig.version} />
                )}
            </Box>
        </ErrorBoundary>
    );
};

export default App;